import { useEffect } from 'react';
import { VyFormSection, VyIcon, VySwitchRow, VyTextField } from '@vy/ui-design-system';
import { PRICING_MODELS, type UsageProductVariant } from '@vy/types';
import { getCurrencySymbol } from '@vy/services';
import { isEmptyString, raiseUnreachable } from '@vy/utils';
import { InputAdornment } from '@mui/material';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import type { FieldPath } from 'react-hook-form';
import { get } from 'lodash';
import { useFormErrorNotificationContext } from '../../../../../../contexts/form-error-notification-context/form-error-notification-context';
import { CREATE_PLAN_TABS, type CreatePlanTab } from '../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { useChargeContext } from '../../charge-context';
import { CommitmentInput, MeterRefillSelect, MeterResetSelect, AutomaticUpgradeSwitch } from '../selectors';
import { FieldGrid } from '../../modify-panel-shared/layout';
import { VyNumberFieldField, parseNonNegativeNumber } from '../../../../../../design-system-adapters/forms';

type UsageVariantFormValues = NonNullable<PlanInputForm['usageProducts']>[number]['variants'];

/** Toggle a Tiers price-config flag across all variants, leaving non-Tiers variants untouched. */
function withTiersPriceConfigFlag(
  variants: UsageVariantFormValues,
  flag: 'isFlatRate' | 'isChunked',
  isEnabled: boolean,
): UsageProductVariant[] {
  return variants.map((usageVariant) => {
    switch (usageVariant.pricing.pricingModel) {
      case PRICING_MODELS.TieredSubscription:
      case PRICING_MODELS.PerUnit:
        return usageVariant;
      case PRICING_MODELS.Tiers:
        return {
          ...usageVariant,
          pricing: {
            ...usageVariant.pricing,
            priceConfiguration: {
              ...usageVariant.pricing.priceConfiguration,
              [flag]: isEnabled,
            },
          },
        };
      default:
        return raiseUnreachable(usageVariant.pricing);
    }
  }) as UsageProductVariant[];
}
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';

export type BillingMeteringSectionProps = {
  showFlatRateSwitch?: boolean;
  showBulkSwitch?: boolean;
  showBulkSizeField?: boolean;
  showAutomaticUpgrade?: boolean;
  isRevShare?: boolean;
};

export function BillingMeteringSection({
  showFlatRateSwitch = false,
  showBulkSwitch = false,
  showBulkSizeField = false,
  showAutomaticUpgrade = false,
  isRevShare = false,
}: BillingMeteringSectionProps) {
  const { itemIndex: productIndex, currency } = useChargeContext();
  const { control, watch, setValue, formState: { errors } } = useFormContext<PlanInputForm>();
  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();

  const { fields: productVariants } = useFieldArray({
    control,
    name: `usageProducts.${productIndex}.variants`,
  });

  const usageVariants = watch(`usageProducts.${productIndex}.variants`) ?? [];

  useEffect(() => {
    registerErrorField(`usageProducts.${productIndex}.accumulationInterval`);
    registerErrorField(`usageProducts.${productIndex}.billingInterval`);
    if (showBulkSizeField) {
      registerErrorField(
        `usageProducts.${productIndex}.variants.0.pricing.priceConfiguration.chunkSize` as FieldPath<PlanInputForm>,
        CREATE_PLAN_TABS.tierAndVolumePricing,
      );
    }
  }, [registerErrorField, productIndex, showBulkSizeField]);

  const isFlatRateEnabled = usageVariants.some((usageVariant) => {
    if (usageVariant.pricing.pricingModel !== PRICING_MODELS.Tiers) {
      return false;
    }
    return usageVariant.pricing.priceConfiguration?.isFlatRate ?? false;
  });

  const isChunkedEnabled = usageVariants.some((usageVariant) => {
    if (usageVariant.pricing.pricingModel !== PRICING_MODELS.Tiers) {
      return false;
    }
    return usageVariant.pricing.priceConfiguration?.isChunked ?? false;
  });

  const handleFlatRateChange = (_event: React.ChangeEvent<HTMLInputElement>, isEnabled: boolean) => {
    setValue(
      `usageProducts.${productIndex}.variants`,
      withTiersPriceConfigFlag(usageVariants, 'isFlatRate', isEnabled),
    );
  };

  const handleChunkChange = (_event: React.ChangeEvent<HTMLInputElement>, isEnabled: boolean) => {
    setValue(
      `usageProducts.${productIndex}.variants`,
      withTiersPriceConfigFlag(usageVariants, 'isChunked', isEnabled),
    );
  };

  const handleChunkSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    const parsed = Number(rawValue);
    // Empty/zero/negative all mean "no bulk size" — the schema requires a
    // positive chunkSize when present, so persist undefined instead.
    const chunkSize = isEmptyString(rawValue) || Number.isNaN(parsed) || parsed <= 0 ? undefined : parsed;

    productVariants.forEach((_, index) => {
      const chunkSizePath: FieldPath<PlanInputForm> =
        `usageProducts.${productIndex}.variants.${index}.pricing.priceConfiguration.chunkSize`;
      setValue(chunkSizePath, chunkSize);
    });
  };

  const currencySymbol = getCurrencySymbol(currency);
  const currencyAdornment = <InputAdornment position="start">{currencySymbol}</InputAdornment>;

  return (
    <VyFormSection icon={<VyIcon icon="Receipt" width={20} height={20} />} title="Billing & Metering">
      <FieldGrid>
        {!isRevShare && <MeterResetSelect />}

        <CommitmentInput />
        <MeterRefillSelect />

        <VyNumberFieldField<PlanInputForm>
          name={`usageProducts.${productIndex}.basePrice`}
          parse={parseNonNegativeNumber}
          label="Minimum Amount"
          helperText="Baseline charge applied even if actual usage is lower"
          fullWidth
          slotProps={{ input: { startAdornment: currencyAdornment } }}
          data-vy="usage-charge-drawer-base-price"
        />

        {showFlatRateSwitch && (
          <VySwitchRow
            checked={isFlatRateEnabled}
            title="Add Flat rate"
            description="Charge a flat rate based on the usage tier level, rather than a per unit fee"
            onChange={handleFlatRateChange}
            data-field-span="full"
            data-vy="usage-charge-drawer-flat-rate-switch"
          />
        )}

        {showBulkSwitch && (
          <VySwitchRow
            checked={isChunkedEnabled}
            title="Add bulk pricing"
            description="Charge per bulk (package) of units. Price applies to the entire unit pack"
            onChange={handleChunkChange}
            data-field-span="full"
            data-vy="usage-charge-drawer-bulk-pricing-switch"
          />
        )}

        {showBulkSizeField && (
          <Controller
            control={control}
            name={`usageProducts.${productIndex}.variants.0.pricing.priceConfiguration.chunkSize`}
            render={({ field }) => (
              <VyTextField
                value={field.value ?? ''}
                onChange={handleChunkSizeChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                label="Bulk size"
                type="number"
                fullWidth
                error={Boolean(
                  get(
                    errors,
                    `usageProducts.${productIndex}.variants.0.pricing.priceConfiguration.chunkSize`,
                  ),
                )}
                helperText={
                  get(
                    errors,
                    `usageProducts.${productIndex}.variants.0.pricing.priceConfiguration.chunkSize`,
                  )?.message
                }
                data-vy="usage-charge-drawer-bulk-size"
              />
            )}
          />
        )}

        {showAutomaticUpgrade && <AutomaticUpgradeSwitch />}
      </FieldGrid>
    </VyFormSection>
  );
}
